import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { motion, useInView, useAnimation } from 'framer-motion';
import { FaLinkedin, FaTwitter, FaEnvelope, FaQuoteLeft, FaArrowRight, FaCaretRight } from 'react-icons/fa';

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
  
  * {
    box-sizing: border-box;
  }
  
  body {
    margin: 0;
    padding: 0;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    background-color: #f8fafc;
    color: #1a1a1a;
  }
`;

const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
  100% { transform: translateY(0px); }
`;

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const shimmerAnimation = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const LeadershipContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 6rem 2rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -200px;
    right: -200px;
    width: 600px;
    height: 600px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
    filter: blur(80px);
    z-index: -1;
    animation: ${floatAnimation} 15s ease-in-out infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -200px;
    left: -200px;
    width: 500px;
    height: 500px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.05));
    filter: blur(80px);
    z-index: -1;
    animation: ${floatAnimation} 18s ease-in-out infinite reverse;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 6rem;
  position: relative;
`;

const Title = styled(motion.h2)`
  font-size: 4rem;
  font-weight: 900;
  color: #1a1a1a;
  margin-bottom: 1.5rem;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.2;
  animation: ${gradientAnimation} 5s ease infinite;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #3b82f6, #8b5cf6);
    border-radius: 2px;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.5s ease;
  }

  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.4rem;
  color: #4b5563;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  font-weight: 400;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2.5rem;
  margin-top: 3rem;
`;

const TeamCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.1), 0 10px 20px -5px rgba(0, 0, 0, 0.05);
  transition: all 0.4s ease;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.5);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 35px 60px -12px rgba(0, 0, 0, 0.15), 0 15px 25px -5px rgba(0, 0, 0, 0.08);
  }
`;

const CardImage = styled(motion.div)`
  height: 380px;
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.8s ease;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.7) 100%);
    opacity: 0.7;
    z-index: 1;
    transition: opacity 0.4s ease;
  }
`;

const CardContent = styled.div`
  padding: 2rem;
  position: relative;
  z-index: 2;
`;

const CardName = styled.h3`
  font-size: 1.8rem;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const CardRole = styled.p`
  font-size: 1.1rem;
  color: #8b5cf6;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  
  &::before {
    content: '';
    display: inline-block;
    width: 12px;
    height: 2px;
    background: #8b5cf6;
    margin-right: 10px;
  }
`;

const CardBio = styled.p`
  font-size: 1rem;
  color: #4b5563;
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SocialIcon = styled(motion.a)`
  width: 45px;
  height: 45px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.1));
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8b5cf6;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, #8b5cf6, #3b82f6);
    color: white;
    transform: translateY(-3px);
    box-shadow: 0 10px 20px -5px rgba(139, 92, 246, 0.4);
  }
`;

const QuoteOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.9), rgba(139, 92, 246, 0.9));
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2.5rem;
  opacity: 0;
  transition: all 0.4s ease;
  z-index: 3;
  backdrop-filter: blur(5px);
`;

const QuoteText = styled.blockquote`
  color: white;
  font-size: 1.2rem;
  line-height: 1.7;
  position: relative;
  padding: 0 0 0 2.5rem;
  margin-bottom: 1.5rem;
  font-weight: 500;
  
  svg {
    position: absolute;
    left: 0;
    top: -0.5rem;
    opacity: 0.5;
    font-size: 2rem;
  }
`;

const QuoteAuthor = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-style: italic;
  font-size: 1rem;
  display: flex;
  align-items: center;
  
  &::before {
    content: '';
    display: inline-block;
    width: 20px;
    height: 2px;
    background: rgba(255, 255, 255, 0.5);
    margin-right: 10px;
  }
`;

const ViewAllButton = styled(motion.button)`
  display: block;
  margin: 4rem auto 0;
  padding: 1rem 2.5rem;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 20px -5px rgba(139, 92, 246, 0.4);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: all 0.6s ease;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 25px -5px rgba(139, 92, 246, 0.5);
    
    &::before {
      left: 100%;
    }
  }
  
  span {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const FloatingOrb = styled.div`
  position: absolute;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(139, 92, 246, 0.2));
  filter: blur(20px);
  top: ${props => props.top || 'auto'};
  left: ${props => props.left || 'auto'};
  right: ${props => props.right || 'auto'};
  bottom: ${props => props.bottom || 'auto'};
  animation: ${floatAnimation} ${props => props.duration || '20s'} ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};
  z-index: -1;
`;

// Sample leadership data
const teamMembers = [
  {
    id: 1,
    name: 'Lakshman sir',
    role: 'CEO & Founder',
    bio: 'Visionary leader with 15+ years of industry experience driving innovation and growth across multiple sectors.',
    image: '/assets/sir2.JPG',
    social: {
      linkedin: '#',
      twitter: '#',
      email: '#'
    },
    quote: "Our mission is to create technology that makes people's lives better. We're not just building products; we're building the future."
  },
  {
    id: 2,
    name: 'Sarah Williams',
    role: 'Chief Technology Officer',
    bio: 'Tech strategist passionate about building scalable systems and mentoring engineering teams to achieve excellence.',
    image: '/assets/s1.JPG',
    social: {
      linkedin: '#',
      twitter: '#',
      email: '#'
    },
    quote: "Great technology should be invisible - it just works when you need it. Our goal is to create seamless experiences that empower users."
  },
  {
    id: 3,
    name: 'Michael Chen',
    role: 'Chief Marketing Officer',
    bio: 'Creative marketer with a knack for storytelling and brand positioning in digital spaces. Expert in growth strategies.',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
    social: {
      linkedin: '#',
      twitter: '#',
      email: '#'
    },
    quote: "Marketing is no longer about the stuff you make, but the stories you tell. Authentic narratives create lasting connections with audiences."
  },
  {
    id: 4,
    name: 'Priya Patel',
    role: 'Chief Product Officer',
    bio: 'Product leader focused on user-centric design and data-driven decision making. Passionate about creating meaningful products.',
    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
    social: {
      linkedin: '#',
      twitter: '#',
      email: '#'
    },
    quote: "The best products solve real problems with elegant simplicity. We listen to users and iterate until we've created something truly valuable."
  },
  {
    id: 5,
    name: 'David Johnson',
    role: 'Chief Financial Officer',
    bio: 'Strategic financial expert with extensive experience in scaling businesses and managing investments for long-term growth.',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
    social: {
      linkedin: '#',
      twitter: '#',
      email: '#'
    },
    quote: "Financial health isn't just about numbers—it's about creating sustainable value for all stakeholders and enabling innovation."
  },
  {
    id: 6,
    name: 'Elena Rodriguez',
    role: 'VP of Engineering',
    bio: 'Seasoned engineering leader with expertise in building high-performing teams and delivering complex technical solutions.',
    image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
    social: {
      linkedin: '#',
      twitter: '#',
      email: '#'
    },
    quote: "Engineering excellence comes from empowering teams, embracing challenges, and never stopping the pursuit of better solutions."
  }
];

const Leadership = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <>
      <GlobalStyle />
      <LeadershipContainer ref={ref}>
        <FloatingOrb top="10%" left="5%" duration="25s" delay="0s" />
        <FloatingOrb top="60%" right="8%" duration="30s" delay="2s" />
        <FloatingOrb bottom="15%" left="15%" duration="22s" delay="1s" />
        
        <Header>
          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            Our Leadership
          </Title>
          <Subtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
          >
            Meet the visionary team driving our company forward with expertise, passion, and dedication to excellence.
          </Subtitle>
        </Header>

        <TeamGrid
          variants={containerVariants}
          initial="hidden"
          animate={controls}
        >
          {teamMembers.map((member) => (
            <TeamCard
              key={member.id}
              variants={cardVariants}
              onMouseEnter={() => setHoveredCard(member.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <CardImage>
                <motion.img
                  src={member.image}
                  alt={member.name}
                  animate={{ scale: hoveredCard === member.id ? 1.1 : 1 }}
                  transition={{ duration: 0.5 }}
                />
                <QuoteOverlay
                  animate={{ opacity: hoveredCard === member.id ? 1 : 0 }}
                >
                  <QuoteText>
                    <FaQuoteLeft />
                    {member.quote}
                  </QuoteText>
                  <QuoteAuthor>- {member.name}</QuoteAuthor>
                </QuoteOverlay>
              </CardImage>
              <CardContent>
                <CardName>{member.name}</CardName>
                <CardRole>{member.role}</CardRole>
                <CardBio>{member.bio}</CardBio>
                <SocialLinks>
                  <SocialIcon
                    href={member.social.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ y: -3 }}
                  >
                    <FaLinkedin />
                  </SocialIcon>
                  <SocialIcon
                    href={member.social.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ y: -3 }}
                  >
                    <FaTwitter />
                  </SocialIcon>
                  <SocialIcon
                    href={`mailto:${member.social.email}`}
                    whileHover={{ y: -3 }}
                  >
                    <FaEnvelope />
                  </SocialIcon>
                </SocialLinks>
              </CardContent>
            </TeamCard>
          ))}
        </TeamGrid>

        <ViewAllButton
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <span>View Full Team <FaCaretRight /></span>
        </ViewAllButton>
      </LeadershipContainer>
    </>
  );
};

export default Leadership;